const DEVICE_NOT_FOUND = 'devicesNotFoundError';
const DEVICE_ALREADY_IN_USE = 'deviceAlreadyInUse';
const PERMISSION_DENIED = 'permissionDenied';
const UNKNOWN = 'unknown';

class StreamError {
  constructor(wrappedError) {
    this.name = this.mapError(wrappedError);
    this.message = wrappedError.message;
  }

  mapError({ name }) {
    // most common errors explained here: https://blog.addpipe.com/common-getusermedia-errors/
    let errorKindOf;

    switch (name) {
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        errorKindOf = DEVICE_NOT_FOUND;
        break;
      case 'NotReadableError':
      case 'TrackStartError':
        errorKindOf = DEVICE_ALREADY_IN_USE;
        break;
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        errorKindOf = PERMISSION_DENIED;
        break;
      default:
        errorKindOf = UNKNOWN;
        break;
    }
    return errorKindOf;
  }
}

class VideoCaptureAPI {
  static DEVICE_NOT_FOUND = DEVICE_NOT_FOUND;
  static DEVICE_ALREADY_IN_USE = DEVICE_ALREADY_IN_USE;
  static PERMISSION_DENIED = PERMISSION_DENIED;
  static UNKNOWN = UNKNOWN;

  static getScreenStream(audioDeviceId) {
    const constraints = {
      video: {
        cursor: 'always',
      },
    };

    return navigator.mediaDevices
      ?.getDisplayMedia(constraints)
      .then(async (displayStream) => {
        const [videoTrack] = displayStream.getVideoTracks();
        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
        });
        const [audioTrack] = audioStream.getAudioTracks();
        return new MediaStream([videoTrack, audioTrack]);
      })
      .catch((e) => {
        throw new StreamError(e);
      });
  }

  static getAudioStream() {
    return navigator.mediaDevices?.getUserMedia({ audio: true, video: false }).catch((e) => {
      throw new StreamError(e);
    });
  }

  static getWebCamStream(deviceIds) {
    const defaultVideoConstrains = {
      width: { min: 640, ideal: 1920, max: 1920 },
      height: { min: 400, ideal: 1080 },
      aspectRatio: 1.777777778,
    };

    const constraints = {
      video: deviceIds?.videoDeviceId
        ? {
            ...defaultVideoConstrains,
            deviceId: { exact: deviceIds.videoDeviceId },
          }
        : defaultVideoConstrains,
      audio: deviceIds?.audioDeviceId ? { deviceId: { exact: deviceIds.audioDeviceId } } : true,
    };

    return navigator.mediaDevices?.getUserMedia(constraints).catch((e) => {
      throw new StreamError(e);
    });
  }

  static stopMediaTracks(stream) {
    if (!stream) {
      return;
    }

    stream.getTracks().forEach((track) => {
      track.stop();
    });
  }

  static getDevices() {
    return navigator.mediaDevices?.enumerateDevices();
  }

  static createRecorder(stream, config) {
    let destroyed = false;
    let recordedChunks = [];
    const stopHandler = () => {
      config.stopHandler && config.stopHandler(recordedChunks);
    };

    let recorder = new MediaRecorder(stream, config.options);

    recorder.ondataavailable = (event) => {
      if (destroyed) {
        return;
      }

      recordedChunks.push(event.data);
    };

    recorder.addEventListener('stop', stopHandler, {
      once: true,
    });

    return {
      start: () => {
        recorder.start(100);
      },
      stop: () => {
        if (recorder.state !== 'inactive') {
          recorder.stop();
        } else {
          stopHandler(recordedChunks);
        }
      },
      pause: () => {
        recorder.pause();
      },
      resume: () => {
        recorder.resume();
      },
      destroy: () => {
        recorder.removeEventListener('stop', stopHandler);
        recorder = null;
        recordedChunks = null;
        destroyed = true;
      },
    };
  }
}

window.VideoCaptureAPI = VideoCaptureAPI;
